import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSignature, UserCheck, Sparkles, Shield, AlertTriangle, Scale, Mail, Ban, Wrench } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8" data-testid="terms-page">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="terms-back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-primary mb-4 md:mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-primary-900 font-heading mb-3 md:mb-4 flex items-center gap-3">
            <FileSignature className="w-8 h-8 md:w-10 md:h-10 text-primary" />
            Conditions Générales d'Utilisation
          </h1>
          <p className="text-slate-600">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4 md:p-8 prose max-w-none">
            {/* 1 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-primary" />
              1. Objet
            </h2>
            <p className="mb-4">
              Les présentes CGU ont pour objet de définir les modalités et conditions dans lesquelles Carelio met à
              disposition de ses utilisateurs professionnels une plateforme web et mobile permettant de gérer le suivi
              médico-social de bénéficiaires : dossiers usagers, notes de service (avec dictée vocale assistée par IA),
              tâches, rendez-vous, messagerie interne et exports PDF.
            </p>

            {/* 2 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-primary" />
              2. Acceptation des CGU
            </h2>
            <p className="mb-4">
              L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU. L'utilisateur reconnaît
              en avoir pris connaissance préalablement à toute inscription. La création d'un compte vaut acceptation
              expresse et sans réserve de l'ensemble des stipulations.
            </p>

            {/* 3 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">3. Description du service</h2>
            <p className="mb-2">Le Service propose notamment :</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Un tableau de bord regroupant l'activité de l'utilisateur (statistiques, conformité, notifications)</li>
              <li>La gestion des bénéficiaires (création, modification, suppression, export PDF)</li>
              <li>La rédaction et l'archivage de notes de service, avec assistance par <strong>dictée vocale et IA</strong></li>
              <li>La gestion des tâches assignées et des rendez-vous</li>
              <li>L'envoi et la réception de notifications applicatives en temps réel</li>
              <li>L'export en PDF de notes, tâches, rendez-vous, fiches bénéficiaires et rapports de conformité</li>
              <li>Une messagerie interne entre membres d'une même organisation</li>
            </ul>

            {/* 4 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-primary" />
              4. Création de compte et accès au Service
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>L'inscription est réservée aux <strong>professionnels majeurs</strong> exerçant dans un établissement médico-social ou en lien avec le secteur</li>
              <li>L'inscription se fait par email/mot de passe ou via <strong>Google OAuth</strong></li>
              <li>Le mot de passe doit comporter <strong>au minimum 8 caractères, au moins une lettre et un chiffre</strong></li>
              <li>L'utilisateur est seul responsable de la confidentialité de ses identifiants et de toute activité réalisée depuis son compte</li>
              <li>Toute utilisation frauduleuse ou suspecte doit être signalée immédiatement à <a href="mailto:contact@carelio.fr" className="text-primary underline">contact@carelio.fr</a></li>
              <li>Carelio se réserve le droit de suspendre ou supprimer tout compte en cas d'usage non conforme aux présentes CGU</li>
            </ul>

            {/* 5 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-primary" />
              5. Phase bêta et niveau de service
            </h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-xl mb-4">
              <p className="text-sm text-yellow-900">
                Carelio est actuellement en <strong>phase bêta gratuite</strong>. Le Service est fourni « <em>en l'état</em> »,
                sans garantie de disponibilité continue, d'absence d'erreur ou de performance commerciale. Aucun engagement
                contractuel de niveau de service (SLA) n'est applicable à cette phase.
              </p>
            </div>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Des interruptions, des évolutions de fonctionnalités ou des modifications de l'interface peuvent survenir sans préavis</li>
              <li>L'utilisateur est invité à conserver ses propres exports (PDF, JSON) à des fins d'archivage indépendant</li>
              <li>Carelio s'efforce néanmoins d'assurer la sécurité, l'intégrité et la confidentialité des données traitées</li>
            </ul>

            {/* 6 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              6. Engagements de l'utilisateur
            </h2>
            <p className="mb-2">L'utilisateur s'engage à :</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Utiliser le Service exclusivement dans un cadre <strong>professionnel</strong> et conformément à sa finalité</li>
              <li>Respecter l'ensemble de la réglementation applicable (RGPD, Code de la santé publique, Code de l'action sociale et des familles, secret professionnel)</li>
              <li>S'assurer du <strong>fondement juridique</strong> de la saisie des données des bénéficiaires (information et, le cas échéant, consentement des personnes concernées ou de leurs représentants légaux)</li>
              <li>Ne pas saisir de données manifestement disproportionnées ou non pertinentes au regard de la prise en charge</li>
              <li>Ne pas tenter de contourner les mesures de sécurité, d'accéder à des comptes tiers, ni de procéder à du <em>scraping</em> ou de l'ingénierie inverse</li>
              <li>Ne pas utiliser le Service pour diffuser des contenus illicites, injurieux, discriminatoires ou portant atteinte aux droits d'autrui</li>
            </ul>

            {/* 7 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              7. Intelligence artificielle et dictée vocale
            </h2>
            <p className="mb-4">
              Le Service intègre des modèles d'IA (<em>Whisper</em> d'OpenAI pour la transcription, <em>Claude Sonnet 4.5</em>
              d'Anthropic pour la génération de texte structuré) afin d'assister la rédaction de notes.
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Les contenus générés par IA ont une valeur <strong>indicative</strong> et doivent obligatoirement faire l'objet d'une <strong>relecture et validation par un professionnel</strong> avant toute utilisation officielle</li>
              <li>Aucune décision automatisée produisant des effets juridiques n'est rendue par l'IA (art. 22 RGPD)</li>
              <li>L'utilisateur reste seul responsable du contenu final qu'il enregistre, partage ou exporte</li>
              <li>L'option <em>no-training</em> est activée auprès des fournisseurs IA : les contenus envoyés ne sont pas utilisés pour entraîner leurs modèles</li>
            </ul>

            {/* 8 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">8. Propriété intellectuelle</h2>
            <p className="mb-2">
              <strong>8.1 Plateforme</strong> — L'ensemble des éléments composant la plateforme Carelio (code, interface,
              marque, charte graphique, logo, contenus rédactionnels) est la propriété exclusive de
              <strong> CREATIVE MEDIA</strong> ou de ses ayants droit. Toute reproduction, représentation ou exploitation
              non autorisée est strictement interdite.
            </p>
            <p className="mb-4">
              <strong>8.2 Contenus utilisateurs</strong> — L'utilisateur conserve l'entière propriété des données et
              contenus qu'il saisit. Il concède à CREATIVE MEDIA une licence strictement limitée à l'exécution du
              Service (stockage, affichage à l'utilisateur, génération de PDF, transmission aux services IA pour
              traitement).
            </p>

            {/* 9 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">9. Données personnelles</h2>
            <p className="mb-4">
              Le traitement des données personnelles est encadré par notre
              <button
                onClick={() => navigate('/confidentialite')}
                className="text-primary underline ml-1"
                data-testid="terms-link-privacy"
              >
                Politique de confidentialité
              </button>
              , qui fait partie intégrante des présentes CGU. L'utilisateur est invité à en prendre connaissance avant
              toute utilisation.
            </p>

            {/* 10 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">10. Tarification</h2>
            <p className="mb-4">
              Pendant la phase bêta, le Service est mis à disposition <strong>gratuitement</strong>. Les conditions
              tarifaires futures seront communiquées par avance et nécessiteront l'acceptation expresse de l'utilisateur
              avant toute facturation.
            </p>

            {/* 11 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Ban className="w-6 h-6 text-primary" />
              11. Durée — Suspension — Résiliation
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Les CGU sont conclues pour une durée indéterminée à compter de la création du compte</li>
              <li>L'utilisateur peut à tout moment résilier son compte en envoyant une demande à <a href="mailto:contact@carelio.fr" className="text-primary underline">contact@carelio.fr</a></li>
              <li>Carelio peut suspendre ou résilier l'accès d'un utilisateur en cas de manquement grave aux présentes CGU, sans préavis ni indemnité</li>
              <li>En cas de résiliation, les données soumises à obligation légale de conservation seront conservées selon les durées indiquées dans la Politique de confidentialité</li>
            </ul>

            {/* 12 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-primary" />
              12. Responsabilité
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Carelio met en œuvre les moyens raisonnables pour assurer la sécurité, la disponibilité et l'intégrité du Service, sans obligation de résultat absolue, en particulier durant la phase bêta</li>
              <li>Carelio ne saurait être tenu responsable des dommages indirects, perte de données, perte d'exploitation ou de chance résultant de l'utilisation du Service</li>
              <li>La responsabilité de Carelio ne pourra excéder, dans la limite permise par la loi, le montant des sommes éventuellement versées par l'utilisateur au cours des 12 derniers mois (soit zéro en phase bêta gratuite)</li>
              <li>L'utilisateur reste seul responsable de l'usage qu'il fait des contenus générés par IA, de la qualité des informations qu'il saisit et de la communication de ces informations à des tiers</li>
            </ul>

            {/* 13 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">13. Modification des CGU</h2>
            <p className="mb-4">
              Carelio se réserve le droit de modifier à tout moment les présentes CGU pour les adapter aux évolutions
              légales, techniques ou fonctionnelles. Toute modification substantielle sera portée à la connaissance des
              utilisateurs par email et/ou via la plateforme. À défaut d'opposition dans un délai raisonnable suivant la
              notification, les nouvelles CGU seront réputées acceptées.
            </p>

            {/* 14 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Scale className="w-6 h-6 text-primary" />
              14. Loi applicable et juridiction
            </h2>
            <p className="mb-4">
              Les présentes CGU sont régies par le <strong>droit français</strong>. En cas de litige, et après tentative
              de résolution amiable, les tribunaux compétents seront ceux du ressort du siège social de CREATIVE MEDIA
              (<strong>Orléans, Loiret</strong>), sous réserve des dispositions impératives applicables aux consommateurs.
            </p>

            {/* 15 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-primary" />
              15. Contact
            </h2>
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="mb-1">Pour toute question relative aux présentes CGU :</p>
              <p className="mb-1"><strong>Éditeur :</strong> CREATIVE MEDIA — SIRET 507 909 307 00054</p>
              <p className="mb-1"><strong>Adresse :</strong> 10 Place de l'Église, 45450 Donnery</p>
              <p><strong>Email :</strong> <a href="mailto:contact@carelio.fr" className="text-primary underline">contact@carelio.fr</a></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsPage;
